import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import ComplexExpressionButton from '../core/ComplexExpressionButton';
import Errored from '../core/Errored';
import ExternalResourceLink from '../core/ExternalResourceLink';
import Loading from '../core/Loading';
import Paging from '../core/Paging';
import RestrictionButton from '../core/RestrctionButton';
import TitledPanel from '../core/TitledPanel';
import { BN_NAMESPACE, MAX_EXCLUDE_RESOURCES } from '../core/utils/constants';
import { getArrayValues, makeCancelable, sortObjects } from '../core/utils/utils';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import GS from '../grafsync/grafsync';
import BlankNodeHandler from './BlankNodeHandler';
import FindResourceDialog from './FindResourceDialog';
import DatatypeDialog from './DatatypeDialog';

const PAGE_SIZE = 500;
const MAX_NODES = 5000;

class ResourceRelationshipsDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      isLoading: props.values ? props.values.length > 0 : false,
      initialLoading: props.values ? props.values.length > 0 : false,
      totalRelations: props.values ? props.values.length : 0,
      resources: undefined,
      viewLang: undefined,
      isErrored: undefined
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {

    if (!state.isLoading && props.values && (props.values.length !== state.totalRelations || props.viewLang !== state.viewLang)) {
      // If a deletion happens that reduces page count and we're on the last page we reset page to page count
      const pageCount = Math.floor((props.values.length + PAGE_SIZE - 1) / PAGE_SIZE);
      let page = state.page;
      if (page > pageCount && pageCount > 0) page = pageCount;
      else if (page > pageCount && pageCount === 0) page = 1;

      return props.values.length === 0 ?
        {
          resources: [],
          isLoading: false,
          page,
          viewLang: props.viewLang,
          totalRelations: 0
        }
        :
        {
          resources: undefined,
          isLoading: true,
          page,
          viewLang: props.viewLang,
          totalRelations: props.values.length
        };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isErrored && this.state.resources === undefined && this.props.values && this.props.values.length > 0) {
      this.collectResourceData();
    }
  }

  componentDidMount() {
    if (this.props.values && this.props.values.length > 0) {
      this.collectResourceData();
    }
    else {
      this.setState({ isLoading: false });
    }
  }

  loadData(iris) {
    this.cancellable = makeCancelable(GS.loadData(iris, {}, this.props.graph));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  addRestriction = async () => {
    const restrictionIRI = BN_NAMESPACE + uuidv4();
    let newRestriction = {
      id: restrictionIRI,
      type: "Restriction",
      invisibility: this.props.ontologyIRI
    };
    await GS.insertResource(newRestriction, restrictionIRI, this.props.graph, [this.props.ontologyIRI]);
    GS.addProperty(this.props.source, this.props.graph, this.props.property, restrictionIRI);
  };

  addComplexClass = async () => {
    const { graph } = this.props;
    const classIRI = BN_NAMESPACE + uuidv4();
    let newClass = {
      id: classIRI,
      invisibility: this.props.ontologyIRI,
      type: "OwlClass"
    };
    // We need to make sure the subject is aware of this as it will be loaded immediately (so before hits DB maybe)
    await GS.insertResource(newClass, classIRI, graph, [this.props.ontologyIRI]);
    GS.addProperty(this.props.source, this.props.graph, this.props.property, classIRI);
  };

  addComplexProperty = async () => {
    const { graph } = this.props;
    const expressionIRI = BN_NAMESPACE + uuidv4();
    let newPropertyExpression = {
      id: expressionIRI,
      invisibility: this.props.ontologyIRI,
      type: "InverseObjectProperty"
    };
    // We need to make sure the subject is aware of this as it will be loaded immediately (so before hits DB maybe)
    await GS.insertResource(newPropertyExpression, expressionIRI, graph, [this.props.ontologyIRI]);
    GS.addProperty(this.props.source, graph, this.props.property, expressionIRI);
  };

  addComplexDataRange = async () => {
    const { graph } = this.props;
    const datatypeIRI = BN_NAMESPACE + uuidv4();
    let newDatatype = {
      id: datatypeIRI,
      invisibility: this.props.ontologyIRI,
      type: "RdfsDatatype"
    };
    await GS.insertResource(newDatatype, datatypeIRI, graph, [this.props.ontologyIRI]);
    GS.addProperty(this.props.source, graph, this.props.property, datatypeIRI);
  };

  async collectResourceData() {
    // We load ALL the resources necessary at this level in the tree in one API call to avoid unnecessary calls
    // There may be more than 5000 children so we need to load in pages just in case!
    try {
      GS.handle("collect-resources");
      const pages = Math.floor((this.props.values.length + MAX_NODES - 1) / MAX_NODES);
      for (let page = 0; page < pages; page++) {
        const pageStart = page * MAX_NODES;
        const pageEnd = (page + 1) * MAX_NODES;
        const pageChildren = this.props.values.slice(pageStart, pageEnd);
        await this.loadData(pageChildren).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
      }
      // Once all loaded then we sort
      this.processResources();
      if (this.cancellable && !this.cancellable.isCancelled()) {
        this.setState({ isLoading: false, initialLoading: false });
      }
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  }

  processResources() {
    let resources = this.props.values.map(id => {
      // Things like XSD namespace won't necessarily be found
      if (!GS.isLoaded(id, this.props.graph)) {
        return { id, notFound: true };
      }
      const data = GS.data(id, this.props.graph);
      if (!data.type) return { id, notFound: true };
      const dataTypeList = getArrayValues(data, "type");
      if (dataTypeList.includes("Restriction") || dataTypeList.includes("InverseObjectProperty") || dataTypeList.includes("RdfsDatatype")) {
        return data;
      }
      else if (this.props.permittedExternalType && !dataTypeList.includes(this.props.permittedExternalType)) {
        return { id, notFound: true, invalidType: true };
      }
      return data;
    });
    sortObjects(resources, 'label', this.props.viewLang, this.props.defaultLang, false);
    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ resources });
  }

  monitorLinkUpdate = (iri, property) => {
    if (property === "label") this.processResources();
  };

  handlePageChange = async (page = 1) => this.setState({ page });

  render() {

    const { view, title, isReadOnly, defaultLang, source, project, graph, includeSystemGraph, permittedExternalType, allowAdd,
      viewLang, highlight, permitExternal, searchExcludeClass, values, restrictionProperty, expressions, target, maxValues, sourceType,
      excludeByPath, excludeByPathList, property, addNewResource, ontologyIRI, searchClass, dataTest, components, useWorkflow } = this.props;
    const { resources, page, isErrored, initialLoading } = this.state;
    if (isErrored) return <Errored dataTest="rr-error" data={isErrored} />;

    const excludedResources = resources ? resources.map(resource => resource.id) : [];
    const exclusions = this.props.exclude ?
      this.props.exclude.filter(exc => !excludedResources.includes(exc))
      :
      [];
    const pageCount = resources ? Math.floor((resources.length + PAGE_SIZE - 1) / PAGE_SIZE) : 0;
    const startNode = (page - 1) * PAGE_SIZE;

    return (
      <TitledPanel
        dataTest={dataTest}
        height="full"
        title={title + (resources && resources.length > 500 ? ` (${resources.length})` : '')}
        highlight={highlight}
        actions=
        {!isReadOnly && !initialLoading && (!resources || resources.length < MAX_EXCLUDE_RESOURCES) ?
          <React.Fragment>
            {restrictionProperty === true &&
              <span style={{ marginRight: 6 }}>
                <RestrictionButton dataTest="add-restriction" onClick={this.addRestriction} title="Add a restriction" />
              </span>
            }
            {expressions === true && view === "class" &&
              <span style={{ marginRight: 6 }}>
                <ComplexExpressionButton dataTest="add-complex-class" onClick={this.addComplexClass} title="Add a complex class expression" />
              </span>
            }
            {expressions === true && view === "objectProperty" &&
              <span style={{ marginRight: 6 }}>
                <ComplexExpressionButton dataTest="add-complex-property" onClick={this.addComplexProperty} title="Add a complex property expression" />
              </span>
            }
            {expressions === true && (view === "datatypeProperty" || view === "datatype") &&
              <span style={{ marginRight: 6 }}>
                <ComplexExpressionButton dataTest="add-complex-datarange" onClick={this.addComplexDataRange} title="Add a complex data range expression" />
              </span>
            }
            {allowAdd === true && (maxValues === undefined || !resources || (resources && resources.length < maxValues)) &&
              <span>
                {target === "IRI" ?
                  <FindResourceDialog
                    key={viewLang}
                    source={source}
                    permitExternal={permitExternal}
                    permittedExternalType={permittedExternalType}
                    components={components}
                    defaultLang={defaultLang}
                    useWorkflow={useWorkflow}
                    sourceComponent={ontologyIRI}
                    searchExcludeClass={searchExcludeClass}
                    searchClass={searchClass}
                    excludeByPath={excludeByPath}
                    excludeByPathList={excludeByPathList}
                    includeSystemGraph={includeSystemGraph}
                    buttonTitle="Add a resource"
                    title={"Add Resource"}
                    project={project}
                    graph={graph}
                    viewLang={viewLang}
                    exclude={[...exclusions, ...excludedResources]}
                    onAddResource={addNewResource} />
                  :
                  <DatatypeDialog
                    sourceComponent={ontologyIRI}
                    sourceType={sourceType}
                    viewLang={viewLang}
                    graph={graph}
                    onAddDatatype={addNewResource}
                    open={false}
                    title="Add Datatype"
                    name="Datatype" />
                }
              </span>
            }
          </React.Fragment>
          :
          null
        }>
        {pageCount > 1 ?
          <div>
            <Paging showText={false} page={page} pageCount={pageCount} handlePageChange={this.handlePageChange} />
          </div>
          :
          null
        }
        {initialLoading ? <Loading message={values.length > 1000 ? "There are a lot of resources to load!" : ""} /> : null}
        {resources && resources.slice(startNode, startNode + PAGE_SIZE).map((resource, rIndex) => {
          if (resource.notFound) {
            return <ExternalResourceLink
              dataTest={dataTest + "-" + resource.id}
              key={"rel-" + source + "-" + resource.id}
              id={resource.id}
              graph={graph}
              invalid={resource.invalidType}
              isReadOnly={isReadOnly}
              source={source}
              property={property} />;
          }
          else if (resource.id.startsWith(BN_NAMESPACE)) {
            return <div
              data-test="blanknode-container"
              key={"rel-" + source + "-" + resource.id}
              style={{ marginBottom: rIndex < resources.length - 1 ? 12 : 0 }}>
              <BlankNodeHandler
                key={"rel-" + source + "-" + resource.id}
                id={resource.id}
                graph={graph}
                useWorkflow={useWorkflow}
                context={ontologyIRI}
                projectIRI={project}
                top={true}
                // As we're at the relationship rendering level we state must be an object property
                isObjectProperty={true}
                sourceType={sourceType}
                sourceResource={source}
                sourceComponent={ontologyIRI}
                components={components}
                isReadOnly={isReadOnly}
                viewLang={viewLang}
                defaultLang={defaultLang} />
            </div>;
          }
          else return <CommonIriDisplay
            dataTest={dataTest + "-" + resource.id}
            id={resource.id}
            key={"rel-" + source + "-" + resource.id}
            components={components}
            projectIRI={project}
            context={ontologyIRI}
            contextProperty="isDefinedBy"
            handleNotAvailable={true}
            property={property}
            isReadOnly={isReadOnly}
            compact={true}
            graph={graph}
            source={source}
            wrap={false}
            viewLang={viewLang}
            defaultLang={defaultLang} />;
        })}
        {pageCount > 1 ?
          <div>
            <Paging showText={false} page={page} pageCount={pageCount} handlePageChange={this.handlePageChange} />
          </div>
          :
          null
        }
      </TitledPanel>
    );
  }
}

ResourceRelationshipsDisplay.defaultProps = {
  highlight: false,
  permitExternal: false,
  expressions: false,
  target: "IRI",
  allowAdd: true,
  maxValues: undefined
};

ResourceRelationshipsDisplay.propTypes = {
  highlight: PropTypes.bool,
  dataTest: PropTypes.string,
  graph: PropTypes.string.isRequired,
  ontologyIRI: PropTypes.string.isRequired,
  defaultLang: PropTypes.string,
  viewLang: PropTypes.string,
  components: PropTypes.array,
  excludeByPath: PropTypes.array,
  excludeByPathList: PropTypes.array,
  values: PropTypes.array,
  maxValues: PropTypes.number,
  exclude: PropTypes.array,
  allowAdd: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  source: PropTypes.string,
  sourceType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  restrictionProperty: PropTypes.bool,
  expressions: PropTypes.bool,
  useWorkflow: PropTypes.bool,
  permittedExternalType: PropTypes.string,
  permitExternal: PropTypes.bool,
  includeSystemGraph: PropTypes.bool,
  addNewResource: PropTypes.func.isRequired,
  searchExcludeClass: PropTypes.array,
  searchClass: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  view: PropTypes.string.isRequired,
  property: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired
};

export default ResourceRelationshipsDisplay;